import React, { useState } from 'react';
import { useModal } from '@europrocurement/flexy-components';
import { AxiosResponse } from 'axios';

import { FproDosDocFileJsonldDocumentfileRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';

import {
    REMOVE_DOS_DOC_CTX,
    REMOVE_DOS_DOC_FILE_CTX,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import LiasseCheckerForm from '../components/shared/LiasseCheckerForm/LiasseCheckerForm';
import { CreatedLiasseType } from '../providers/types';
import useDossierDocumentFilesApi from './useDossierDocumentFilesApi';

const useFileChecker = () => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { modalActions } = useModal();

    const documentContext = useDossierDocumentsCtx();
    const { removeDosDoc, removeDosDocFile } = useDossierDocumentFilesApi();
    const dossierDocumentsDispatch = useDossierDocumentsCtxDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (filesToRemove: FproDosDocFileJsonldDocumentfileRead[]) => {
        if (isSubmitting) {
            return;
        }
        if (filesToRemove.length > 0) {
            setIsSubmitting(true);

            const validHttpCodes = [200, 204];
            const errors: FproDosDocFileJsonldDocumentfileRead[] = [];

            // Delete FproDosDoc
            await Promise.all(
                filesToRemove.map(async (file) => {
                    if (file.id && file.typeDocDossier?.id) {
                        const fichierDelRepsonse = await removeDosDocFile(file.id);

                        if (validHttpCodes.includes(fichierDelRepsonse.status)) {
                            dossierDocumentsDispatch({
                                type: REMOVE_DOS_DOC_FILE_CTX,
                                payload: { fichierId: file.id },
                            });

                            const docDelRepsonse = await removeDosDoc(file.typeDocDossier.id);

                            if (validHttpCodes.includes(docDelRepsonse.status)) {
                                dossierDocumentsDispatch({
                                    type: REMOVE_DOS_DOC_CTX,
                                    payload: file.typeDocDossier.id,
                                });
                            } else {
                                errors.push(file);
                            }
                        } else {
                            errors.push(file);
                        }
                    } else {
                        errors.push(file);
                    }
                }),
            );

            if (errors.length > 0) {
                errors.map((errorFile) =>
                    enqueueSnackbar(
                        `Une erreur est survenue lors de la suppression du fichier ${errorFile.fichier}`,
                        {
                            variant: 'error',
                        },
                    ),
                );
            }

            setIsSubmitting(false);
            modalActions.close();
        } else {
            setIsSubmitting(false);
            modalActions.close();
        }
    };

    const displayFilesCheckerForm = (
        createdFiles: CreatedLiasseType[],
        getFichierContent: (fichierId: number) => Promise<AxiosResponse<void>>,
    ) => {
        // Modal de vérification de fichiers
        modalActions.call(
            <LiasseCheckerForm
                createdLiasses={createdFiles}
                getStatusIdentifier={documentContext.getStatusIdentifier}
                getFichierContent={getFichierContent}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
            />,
            false,
            true,
        );
    };

    return { displayFilesCheckerForm };
};

export default useFileChecker;
